/**
 * Equipment Shop View
 * Browse and purchase equipment from an outfitter's inventory
 */

import { useState } from 'react';
import { OutfitterDefinition, EquipmentSlot } from '@/types';
import { getEquipmentDefinition } from '@/data';
import { getSlotFilters } from '@/data/equipment/slotConfig';
import { useEquippedGear } from '@/state';
import { EquipmentCard } from '@/components/shop/EquipmentCard';

type SlotFilter = 'all' | EquipmentSlot;

interface EquipmentShopViewProps {
  outfitter: OutfitterDefinition;
}

export function EquipmentShopView({ outfitter }: EquipmentShopViewProps) {
  const [filterSlot, setFilterSlot] = useState<SlotFilter>('all');
  const equippedGear = useEquippedGear();
  const slotFilters = getSlotFilters(equippedGear);

  // Resolve equipment definitions from inventory
  const items = outfitter.inventory
    .map((item) => {
      try {
        return { inventoryItem: item, definition: getEquipmentDefinition(item.equipmentId) };
      } catch {
        return null;
      }
    })
    .filter(Boolean) as { inventoryItem: typeof outfitter.inventory[number]; definition: ReturnType<typeof getEquipmentDefinition> }[];

  // Apply slot filter
  const filtered = filterSlot === 'all'
    ? items
    : items.filter((item) => item.definition.slot === filterSlot);

  // Sort by tier then slot
  const sorted = [...filtered].sort((a, b) => {
    if (a.definition.slot !== b.definition.slot) {
      return a.definition.slot.localeCompare(b.definition.slot);
    }
    return a.definition.tier - b.definition.tier;
  });

  return (
    <div className="space-y-4">
      {/* Slot Filters */}
      <div className="flex gap-2">
        {slotFilters.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilterSlot(f.id)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              filterSlot === f.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Equipment Grid */}
      {sorted.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No equipment available for this filter.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sorted.map((item) => (
            <EquipmentCard key={item.definition.id} equipment={item.definition} />
          ))}
        </div>
      )}
    </div>
  );
}
